import {useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {Collapse, List, ListItemButton, ListItemIcon, ListItemText,} from "@mui/material";
import {
    Activity,
    Archive,
    BriefcaseBusiness,
    Building2,
    CheckSquare,
    ChevronDown,
    ChevronRight,
    Cog,
    FolderOpen,
    MailOpen,
    BarChart3,
    KeyRound,
    LayoutDashboard,
    LayoutTemplate,
    LineChart,
    Megaphone,
    Rocket,
    Settings,
    Shield,
    ShieldAlert,
    Sparkles,
    Store,
    Table2,
    Tags,
    TriangleAlert,
    Upload,
    Users,
    Waypoints,
} from "lucide-react";
import {Paths} from "../../../paths";
import {appStore} from "@store/appStore";
import {useSnapshot} from "valtio";
import {PermissionLevels} from "@models/permission.model";
import {MARKETING_BANNERS, OP_LAUNCH_DASHBOARD, OP_LAUNCH_LIST, OP_REPORTS, useFeatureFlag} from "@/shared/featureFlags/useFeatureFlag";

const ICON_SIZE = 18;

interface NavItemProps {
    to: string;
    icon: React.ReactNode;
    label: string;
    currentPath: string;
}

function NavItem({to, icon, label, currentPath}: NavItemProps) {
    const isActive = currentPath === `/${to}` || currentPath.startsWith(`/${to}/`);

    return (
        <ListItemButton
            component={Link}
            to={to}
            disableRipple
            sx={{
                borderRadius: "8px",
                mx: 1,
                mb: 0.5,
                py: 1,
                // 12, not the MUI default 16 — see the note on ListItemIcon below.
                px: 1.5,
                color: isActive ? "#fff" : "rgba(255,255,255,0.65)",
                backgroundColor: isActive ? "rgba(255,255,255,0.1)" : "transparent",
                "&:hover": {
                    backgroundColor: isActive
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(255,255,255,0.06)",
                    color: "#fff",
                },
                "&:focus, &:focus-visible, &.Mui-focusVisible": {
                    backgroundColor: isActive ? "rgba(255,255,255,0.1)" : "transparent",
                    color: isActive ? "#fff" : "rgba(255,255,255,0.65)",
                },
            }}
        >
            {/* The sidebar's longest label sets these numbers. Measured in Inter at the
                ACTIVE weight of 600 — the weight is the whole problem, since bolding
                "Operations Dashboard" takes it from 141px to 152px — against a text column
                of 240 (drawer) less the root List's px, the Admin indent, this button's
                margin and padding, and this icon:

                    36 icon, 16 padding  ->  146px   6px short; wrapped, then ellipsised
                    28 icon, 12 padding  ->  156px   fits, 4px spare

                The icon is 18px, so 28 still leaves it a 10px gap. */}
            <ListItemIcon sx={{color: "inherit", minWidth: 28}}>
                {icon}
            </ListItemIcon>
            <ListItemText
                primary={label}
                primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 600 : 400,
                    // A nav label is an identifier; two lines of one reads as two entries.
                    // Belt and braces with the width above — if a platform's font fallback
                    // renders wider than measured, this truncates rather than wraps.
                    noWrap: true,
                }}
            />
        </ListItemButton>
    );
}

/**
 * A collapsible group inside the Admin section (MPN-2313). Styled as a NavItem so it reads
 * as one of the entries, with a chevron where an entry has none; its children sit one indent
 * further in.
 */
function NavGroup({ icon, label, open, onToggle, children }: {
    icon: React.ReactNode; label: string; open: boolean; onToggle: () => void; children: React.ReactNode;
}) {
    return (
        <>
            <ListItemButton
                onClick={onToggle}
                disableRipple
                aria-expanded={open}
                sx={{
                    borderRadius: "8px",
                    mx: 1,
                    mb: 0.5,
                    py: 1,
                    px: 1.5,
                    color: "rgba(255,255,255,0.65)",
                    "&:hover": {backgroundColor: "rgba(255,255,255,0.06)", color: "#fff"},
                }}
            >
                <ListItemIcon sx={{color: "inherit", minWidth: 28}}>{icon}</ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{fontSize: "0.875rem", fontWeight: 400, noWrap: true}} />
                {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{pl: 1.5}}>
                    {children}
                </List>
            </Collapse>
        </>
    );
}

export default function Sidebar() {
    const {loggedInUser} = useSnapshot(appStore);
    const location = useLocation();
    const [adminOpen, setAdminOpen] = useState(true);
    const opLaunchListOn = useFeatureFlag(OP_LAUNCH_LIST);
    const opLaunchDashboardOn = useFeatureFlag(OP_LAUNCH_DASHBOARD);
    const bannersOn = useFeatureFlag(MARKETING_BANNERS);
    const opReportsOn = useFeatureFlag(OP_REPORTS);
    // The Old Platform group opens on its own when one of its pages is the page shown.
    const onOldPlatformPage = location.pathname.startsWith(`/${Paths.OP_LAUNCHES}`)
        || location.pathname.startsWith(`/${Paths.OP_LAUNCH_DASHBOARD}`)
        || location.pathname.startsWith(`/${Paths.OP_REPORTS}`);
    const [oldPlatformOpen, setOldPlatformOpen] = useState(onOldPlatformPage);

    const hasSomeAccess = (feature: string, level = PermissionLevels.VIEW) =>
        loggedInUser?.permissions?.hasSomeAccess?.(feature, level) ?? false;
    // The old platform's pages, read from its frozen warehouse (MPN-2229, MPN-2230,
    // MPN-2275), behind their own feature (MPN-2365) so a role can hold them without the
    // Operations Dashboard; each route also has its own flag. Grouped at the bottom of
    // Admin (MPN-2313).
    const oldPlatformOn = hasSomeAccess("Old Platform Reporting") && (opLaunchListOn || opLaunchDashboardOn || opReportsOn);

    return (
        <List component="nav" sx={{px: 0.5, pt: 1}} disablePadding>
            {/* The two client dashboards (MPN-1507). The permission feature names are NOT
                the labels and do not line up with them: "Legacy Dashboard" gates the old
                platform's dashboard, "Dashboard" gates the new platform's. The features are
                matched by string against rows in the permission tables, so they stay as they
                are — only what the client reads changes. */}
            {hasSomeAccess("Legacy Dashboard") && (
                <NavItem
                    to={Paths.CLIENT_DASHBOARD_LEGACY}
                    icon={<LayoutDashboard size={ICON_SIZE} />}
                    label="Legacy Dashboard"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("Dashboard") && (
                <NavItem
                    to={Paths.HOME}
                    icon={<LayoutDashboard size={ICON_SIZE} />}
                    label="Partner Dashboard"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("My Todos") && (
                <NavItem
                    to={Paths.MY_TODOS}
                    icon={<CheckSquare size={ICON_SIZE} />}
                    label="My Todos"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("Engagement Tool", PermissionLevels.UPDATE) && (
                <NavItem
                    to={Paths.ENGAGEMENT}
                    icon={<Users size={ICON_SIZE}/>}
                    label="Engagements"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("Content Catalog") && (
                <NavItem
                    to={Paths.CONTENT_CATALOG}
                    icon={<FolderOpen size={ICON_SIZE}/>}
                    label="Content Catalog"
                    currentPath={location.pathname}
                />
            )}

            {/* Internal to Motimatic (MPN-2201): the templates and sequences every client's
                Email & SMS campaigns draw from. Gated on the same feature the page and the
                server gate their reads on, so the nav never offers a door that opens onto an
                access-denied state. */}
            {hasSomeAccess("Email & SMS") && (
                <NavItem
                    to={Paths.EMAIL_SMS_CATALOG}
                    icon={<MailOpen size={ICON_SIZE}/>}
                    label="Email & SMS Catalog"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("Moti Store") && (
                <NavItem
                    to={Paths.MOTI_STORE}
                    icon={<Store size={ICON_SIZE}/>}
                    label="Moti Store"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("Create Studio") && (
                <NavItem
                    to={Paths.CREATE}
                    icon={<Sparkles size={ICON_SIZE}/>}
                    label="Create"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("Network Presets") && (
                <NavItem
                    to={Paths.NETWORK_TEMPLATES}
                    icon={<LayoutTemplate size={ICON_SIZE}/>}
                    label="Network Templates"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("Ingest Files") && (
                <NavItem
                    to={Paths.UPLOADS}
                    icon={<Upload size={ICON_SIZE}/>}
                    label="Upload"
                    currentPath={location.pathname}
                />
            )}

            {hasSomeAccess("Ingest Files") && (
                <NavItem
                    to={Paths.ACCOUNT_SETUP}
                    icon={<Cog size={ICON_SIZE}/>}
                    label="Account Setup"
                    currentPath={location.pathname}
                />
            )}

            {/* Admin section */}
            {(hasSomeAccess("Organizations") || hasSomeAccess("Permissions") || hasSomeAccess("Jobs") || hasSomeAccess("Tracer") || hasSomeAccess("Config List") || hasSomeAccess("Operations Dashboard") || hasSomeAccess("Ingest Configuration") || hasSomeAccess("Platform Admin") || (bannersOn && hasSomeAccess("Manage Banners")) || oldPlatformOn) && (
                <>
                    <ListItemButton
                        onClick={() => setAdminOpen(!adminOpen)}
                        disableRipple
                        sx={{
                            borderRadius: "8px",
                            mx: 1,
                            mt: 1.5,
                            mb: 0.5,
                            py: 1,
                            color: "rgba(255,255,255,0.45)",
                            "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.06)",
                                color: "rgba(255,255,255,0.65)",
                            },
                        }}
                    >
                        <ListItemIcon sx={{color: "inherit", minWidth: 36}}>
                            <Shield size={ICON_SIZE}/>
                        </ListItemIcon>
                        <ListItemText
                            primary="ADMIN"
                            primaryTypographyProps={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                            }}
                        />
                        {adminOpen ? (
                            <ChevronDown size={16}/>
                        ) : (
                            <ChevronRight size={16}/>
                        )}
                    </ListItemButton>

                    <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{pl: 1}}>
                            {hasSomeAccess("Operations Dashboard") && (
                                <NavItem
                                    to={Paths.OPS_DASHBOARD}
                                    icon={<Activity size={ICON_SIZE}/>}
                                    label="Operations Dashboard"
                                    currentPath={location.pathname}
                                />
                            )}
                            {/* Ingest Configuration: the Ops roles. Not Ingest Files, which
                                customers hold (MPN-2009). */}
                            {hasSomeAccess("Ingest Configuration") && (
                                <NavItem
                                    to={Paths.DATA_ISSUES}
                                    icon={<TriangleAlert size={ICON_SIZE}/>}
                                    label="Data issues"
                                    currentPath={location.pathname}
                                />
                            )}
                            {hasSomeAccess("Organizations") && (
                                <NavItem
                                    to={Paths.ORGANIZATIONS}
                                    icon={<Building2 size={ICON_SIZE}/>}
                                    label="Organizations"
                                    currentPath={location.pathname}
                                />
                            )}
                            {/* Marketing banners on the Partner Dashboard (MPN-2265). */}
                            {bannersOn && hasSomeAccess("Manage Banners") && (
                                <NavItem
                                    to={Paths.BANNERS}
                                    icon={<Megaphone size={ICON_SIZE}/>}
                                    label="Banners"
                                    currentPath={location.pathname}
                                />
                            )}
                            {/* Internal only: every client's lift on one page. Gated on
                                Platform Admin, the same feature the Ops Dashboard uses for a
                                cross-client surface, rather than on anything a customer role
                                holds. */}
                            {/* Its own feature as well as Platform Admin. The report is read
                                by Data Analysts, and the Analytics role does not hold Platform
                                Admin -- so the audience the page names could not see the link
                                to it (MPN-2046). Platform Admin stays in the check so nobody
                                who has the link today loses it. */}
                            {(hasSomeAccess("Holdout Report")
                                || hasSomeAccess("Platform Admin")) && (
                                <NavItem
                                    to={Paths.HOLDOUT_REPORT}
                                    icon={<BarChart3 size={ICON_SIZE}/>}
                                    label="Holdout Test Results"
                                    currentPath={location.pathname}
                                />
                            )}
                            {hasSomeAccess("Permissions") && (
                                <NavItem
                                    to={Paths.PERMISSIONS}
                                    icon={<KeyRound size={ICON_SIZE}/>}
                                    label="Permissions"
                                    currentPath={location.pathname}
                                />
                            )}
                            {hasSomeAccess("Jobs") && (
                                <NavItem
                                    to={Paths.JOBS}
                                    icon={<BriefcaseBusiness size={ICON_SIZE}/>}
                                    label="Jobs"
                                    currentPath={location.pathname}
                                />
                            )}
                            {hasSomeAccess("Tracer") && (
                                <NavItem
                                    to={Paths.TRACER}
                                    icon={<Activity size={ICON_SIZE}/>}
                                    label="Tracer"
                                    currentPath={location.pathname}
                                />
                            )}
                            {hasSomeAccess("Config List") && (
                                <NavItem
                                    to={Paths.CONFIG}
                                    icon={<Settings size={ICON_SIZE}/>}
                                    label="Config List"
                                    currentPath={location.pathname}
                                />
                            )}
                            {hasSomeAccess("Config List") && (
                                <NavItem
                                    to={Paths.CATALOG}
                                    icon={<Tags size={ICON_SIZE}/>}
                                    label="Metric Catalog"
                                    currentPath={location.pathname}
                                />
                            )}
                            {hasSomeAccess("Engagement Tool") && (
                                <NavItem
                                    to={Paths.JOURNEY_STEPS}
                                    icon={<Waypoints size={ICON_SIZE}/>}
                                    label="Journey Steps"
                                    currentPath={location.pathname}
                                />
                            )}
                            {hasSomeAccess("Platform Admin") && (
                                <NavItem
                                    to={Paths.QUARANTINE}
                                    icon={<ShieldAlert size={ICON_SIZE}/>}
                                    label="Quarantine"
                                    currentPath={location.pathname}
                                />
                            )}
                            {oldPlatformOn && (
                                <NavGroup icon={<Archive size={ICON_SIZE}/>} label="Old Platform" open={oldPlatformOpen}
                                    onToggle={() => setOldPlatformOpen(!oldPlatformOpen)}>
                                    {opLaunchListOn && (
                                        <NavItem
                                            to={Paths.OP_LAUNCHES}
                                            icon={<Rocket size={ICON_SIZE}/>}
                                            label="OP Launches"
                                            currentPath={location.pathname}
                                        />
                                    )}
                                    {opLaunchDashboardOn && (
                                        <NavItem
                                            to={Paths.OP_LAUNCH_DASHBOARD}
                                            icon={<LineChart size={ICON_SIZE}/>}
                                            label="OP Launch Dashboard"
                                            currentPath={location.pathname}
                                        />
                                    )}
                                    {opReportsOn && (
                                        <NavItem
                                            to={Paths.OP_REPORTS}
                                            icon={<Table2 size={ICON_SIZE}/>}
                                            label="OP Reports"
                                            currentPath={location.pathname}
                                        />
                                    )}
                                </NavGroup>
                            )}
                        </List>
                    </Collapse>
                </>
            )}
        </List>
    );
}
